// Листы перед текстом («Задание», «Реферат», «Аннотация») в «Разметке страницы»: текст малого редактора перетекает
// на следующий лист A4 по тем же правилам, что и основной текст (measure.ts + engine.ts: построчно, запрет висячих
// строк, «не отрывать от следующего»), распорки - те же виджеты ProseMirror. Раньше лист длиннее A4 показывался одним
// белым листом высотой 2-3 страницы без полей и промежутков. Верх текста на следующих листах - поле 20 мм (у листов
// перед текстом в DOCX нет колонтитула). Страница курсора в малом редакторе - в строку состояния и навигатор.
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DecorationSet, type EditorView } from '@tiptap/pm/view';
import { Walker } from './measure';
import { paginate } from './engine';
import { M_TOP, PAGE_W, STRIDE } from './geometry';
import { buildSpacers, spacerDeco, sameSpacers, CONT_LABEL_H } from './plugin';
import type { Spacer } from './types';
import { pagesStore } from './store';

interface FState { deco: DecorationSet; spacers: Spacer[] }
const key = new PluginKey<FState>('stoFrontPages');

function hostOf(view: EditorView): HTMLElement | null {
  return (view.dom as HTMLElement).closest('.pg-front') as HTMLElement | null;
}

/** Разметка малого редактора на листе перед текстом; число страниц - в атрибут листа data-flow-pages. */
export function frontLayout(view: EditorView): void {
  if (view.isDestroyed) return;
  const st = key.getState(view.state);
  const host = hostOf(view);
  if (!st) return;
  if (!host || !view.dom.isConnected) {
    if (st.spacers.length) view.dispatch(view.state.tr.setMeta(key, { deco: DecorationSet.empty, spacers: [] }).setMeta('addToHistory', false));
    return;
  }
  const R = host.getBoundingClientRect();
  if (!R.width) return;
  const k = R.width / PAGE_W;
  const w = new Walker(view, R, k);
  w.lastDBottom = (view.dom.getBoundingClientRect().top - R.top) / k;
  w.container(view.dom as HTMLElement, null);
  w.finish();
  if (!w.atoms.length) return;
  const pages = paginate(w.atoms, { fnHeight: () => 0, fnSep: 0, contLabelH: CONT_LABEL_H, top: M_TOP, firstInPlace: true });
  const spacers = buildSpacers(w.atoms, pages);
  if (!sameSpacers(spacers, st.spacers)) {
    view.dispatch(view.state.tr.setMeta(key, { deco: DecorationSet.create(view.state.doc, spacers.map(spacerDeco)), spacers })
      .setMeta('addToHistory', false));
  }
  if (host.getAttribute('data-flow-pages') !== String(pages.length)) host.setAttribute('data-flow-pages', String(pages.length));
  (window as any).__stoFrontPages = { ...(window as any).__stoFrontPages, [host.getAttribute('data-sheet') || '?']: pages.length };
}

/** Страница курсора малого редактора (номер в документе). */
function frontCurrent(view: EditorView) {
  const host = hostOf(view);
  if (!host || !view.hasFocus()) return;
  try {
    const c = view.coordsAtPos(view.state.selection.head);
    const R = host.getBoundingClientRect();
    const k = R.width / PAGE_W || 1;
    const idx = Math.max(0, Math.floor(((c.top + c.bottom) / 2 - R.top) / k / STRIDE));
    const s = pagesStore.getState();
    const id = host.getAttribute('data-sheet');
    let n = 0;
    for (const f of s.front) { if (f.id === id) { n += Math.min(idx, f.pages - 1) + 1; break; } n += f.pages; }
    if (n && n !== s.current) s.set({ current: n });
  } catch { /* нет координат */ }
}

class FrontView {
  raf = 0;
  ro: ResizeObserver | null = null;
  constructor(public view: EditorView) {
    this.schedule();
    try {
      this.ro = new ResizeObserver(() => this.schedule());
      this.ro.observe(view.dom);
    } catch { /* нет ResizeObserver */ }
    (document as any).fonts?.ready?.then(() => this.schedule());
  }
  schedule() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => { this.raf = 0; frontLayout(this.view); });
  }
  update(view: EditorView, prev: any) {
    if (view.state.doc !== prev.doc) this.schedule();
    else if (view.state.selection !== prev.selection) frontCurrent(view);
  }
  destroy() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.ro?.disconnect();
  }
}

export const FrontPagesExtension = Extension.create({
  name: 'stoFrontPages',
  addProseMirrorPlugins() {
    return [new Plugin<FState>({
      key,
      state: {
        init: () => ({ deco: DecorationSet.empty, spacers: [] }),
        apply(tr, prev) {
          const m = tr.getMeta(key);
          if (m) return m;
          if (tr.docChanged) return { deco: prev.deco.map(tr.mapping, tr.doc), spacers: prev.spacers.map((s) => ({ ...s, pos: tr.mapping.map(s.pos, -1) })) };
          return prev;
        },
      },
      props: {
        decorations: (s) => key.getState(s)?.deco,
        handleDOMEvents: { focus: (view) => { requestAnimationFrame(() => frontCurrent(view)); return false; } },
      },
      view: (v) => new FrontView(v),
    })];
  },
});
